// iter568 — Auditor Audit-Trail page: aggregates every Month-End Note
// across all bank statements into a single filterable + printable timeline.
// Designed to be handed to the accountant or external auditor.

import { cur } from '@/lib/currency';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { Bookmark, Printer, Search, Filter, Sparkles, BookOpen, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

function fmtSar(n) {
  return `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ymLabel(ym) {
  if (!ym || ym.length < 7) return ym || '—';
  const [y, m] = ym.split('-');
  return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
}

export default function MonthEndAuditTimelinePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ notes: [], by_month: {}, by_branch: {}, by_source: {}, total_discrepancies: 0, total_gap: 0 });
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchFilter) params.branch_id = branchFilter;
      if (monthFilter) params.ym = monthFilter;
      if (sourceFilter) params.source = sourceFilter;
      const r = await api.get('/month-end-notes/all', { params });
      setData(r.data || { notes: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load notes');
      setData({ notes: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchFilter, monthFilter, sourceFilter]);

  useEffect(() => {
    api.get('/branches').then((r) => setBranches(r.data || [])).catch(() => setBranches([]));
  }, []);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || (id ? 'Unknown branch' : '—');

  const filtered = useMemo(() => {
    const s = (search || '').trim().toLowerCase();
    if (!s) return data.notes || [];
    return (data.notes || []).filter((n) =>
      (n.title || '').toLowerCase().includes(s)
      || (n.narrative || '').toLowerCase().includes(s)
      || (n.bank_name || '').toLowerCase().includes(s)
      || (branchName(n.branch_id) || '').toLowerCase().includes(s)
    );
  }, [data.notes, search, branches]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const n of filtered) {
      const ym = (n.created_at || '').slice(0, 7) || '0000-00';
      if (!map.has(ym)) map.set(ym, []);
      map.get(ym).push(n);
    }
    return Array.from(map.entries()).sort(([a], [b]) => b.localeCompare(a));
  }, [filtered]);

  const months = Object.keys(data.by_month || {}).sort().reverse();

  const downloadPdf = () => {
    // Print-to-PDF using the browser's native print dialog.
    window.print(); // eslint-print-native-ok — audit timeline PDF export via browser dialog
  };
  const downloadServerPdf = async () => {
    // iter572 — Server-side WeasyPrint render. Doesn't depend on the
    // browser print dialog, so it's emailable / can be stored as an
    // audit artefact.
    try {
      const params = {};
      if (branchFilter) params.branch_id = branchFilter;
      if (monthFilter) params.ym = monthFilter;
      if (sourceFilter) params.source = sourceFilter;
      const r = await api.get('/month-end-notes/export-pdf', {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monthly-close-pack${monthFilter ? `-${monthFilter}` : ''}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Monthly Close Pack downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Server PDF render failed');
    }
  };

  const deleteNote = async (note) => {
    if (!window.confirm(`Delete this month-end note?\n"${note.title}"\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/bank-statements/${note.statement_id}/month-end-notes/${note.id}`);
      toast.success('Note deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 audit-trail-print-root" data-testid="audit-timeline-root">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap audit-trail-header">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <BookOpen size={22} className="text-emerald-600" />
              Month-End Audit Trail
            </h1>
            <p className="text-sm text-muted-foreground">
              Every saved Month-End Note across every bank statement — your auditor-ready monthly close pack.
            </p>
          </div>
          <div className="flex items-center gap-2 audit-trail-actions">
            <Button
              variant="outline"
              size="sm"
              onClick={downloadServerPdf}
              data-testid="audit-download-pdf-btn"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              title="Download a clean A4 PDF rendered on the server (works without the browser print dialog)"
            >
              <Bookmark size={14} className="mr-1" /> Download PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadPdf}
              data-testid="audit-print-btn"
              title="Open the browser print dialog for a quick print"
            >
              <Printer size={14} className="mr-1" /> Print
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="audit-stats">
          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Notes</div>
              <div className="text-2xl font-bold text-emerald-800" data-testid="audit-stat-notes">{data.count || 0}</div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50/40">
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Discrepancies analysed</div>
              <div className="text-2xl font-bold text-blue-800" data-testid="audit-stat-discrepancies">{data.total_discrepancies || 0}</div>
            </CardContent>
          </Card>
          <Card className="border-rose-200 bg-rose-50/40">
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Combined gap</div>
              <div className="text-2xl font-bold text-rose-800" data-testid="audit-stat-gap">{fmtSar(data.total_gap)}</div>
            </CardContent>
          </Card>
          <Card className="border-purple-200 bg-purple-50/40">
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">AI-generated</div>
              <div className="text-2xl font-bold text-purple-800 flex items-center gap-1" data-testid="audit-stat-ai">
                <Sparkles size={18} /> {data.by_source?.ai_batch_explain || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="audit-trail-filters">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Filter size={14} /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground">Search</label>
                <div className="relative">
                  <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-7 h-9 text-xs"
                    placeholder="Title, body, bank, branch..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="audit-search"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Branch</label>
                <Select value={branchFilter || 'all'} onValueChange={(v) => setBranchFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-9 text-xs" data-testid="audit-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Month</label>
                <Select value={monthFilter || 'all'} onValueChange={(v) => setMonthFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-9 text-xs" data-testid="audit-month-filter"><SelectValue placeholder="All months" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All months</SelectItem>
                    {months.map((m) => (
                      <SelectItem key={m} value={m}>{ymLabel(m)} ({data.by_month[m]})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Source</label>
                <Select value={sourceFilter || 'all'} onValueChange={(v) => setSourceFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-9 text-xs" data-testid="audit-source-filter"><SelectValue placeholder="All sources" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sources</SelectItem>
                    <SelectItem value="ai_batch_explain">AI batch-explain</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        {loading ? (
          <Card><CardContent className="p-12 text-center text-muted-foreground text-sm">Loading audit timeline…</CardContent></Card>
        ) : grouped.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-muted-foreground">
              <Bookmark size={32} className="mx-auto mb-3 opacity-40" />
              <div className="text-sm font-medium">No Month-End Notes yet</div>
              <div className="text-xs mt-1">Open a bank statement → Reconciliation → AI Explain All → Save as Month-End Note.</div>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/bank-statements')} data-testid="audit-empty-cta">
                Go to Bank Statements
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {grouped.map(([ym, notes]) => (
              <div key={ym} data-testid={`audit-month-${ym}`}>
                <div className="flex items-center gap-2 mb-2 sticky top-0 bg-background/95 backdrop-blur z-10 py-1 audit-trail-month-header">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{ymLabel(ym)}</h2>
                  <Badge variant="secondary" className="font-normal">{notes.length} note{notes.length === 1 ? '' : 's'}</Badge>
                </div>
                <div className="space-y-2 pl-3 border-l-2 border-emerald-200">
                  {notes.map((n) => (
                    <Card key={n.id} className="border-emerald-100 bg-emerald-50/30" data-testid={`audit-note-${n.id}`}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold flex items-center justify-between gap-2 flex-wrap">
                          <span className="flex items-center gap-2 min-w-0">
                            <Bookmark size={14} className="text-emerald-700 shrink-0" />
                            <span className="truncate" title={n.title}>{n.title}</span>
                            {n.source === 'ai_batch_explain' && (
                              <Badge variant="outline" className="text-[9px] font-normal border-purple-300 text-purple-700">AI</Badge>
                            )}
                          </span>
                          <div className="flex items-center gap-2 text-[10px] font-normal text-muted-foreground audit-trail-note-meta">
                            <span>{(n.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                            {n.created_by_name && <span>· {n.created_by_name}</span>}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/bank-statements?stmt=${n.statement_id}`)}
                              className="h-6 px-1.5 text-[10px] audit-trail-no-print"
                              data-testid={`audit-open-stmt-${n.id}`}
                              title="Open the originating bank statement"
                            >
                              <ExternalLink size={11} className="mr-0.5" /> Open stmt
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteNote(n)}
                              className="h-6 w-6 p-0 text-rose-600 hover:bg-rose-50 audit-trail-no-print"
                              data-testid={`audit-delete-${n.id}`}
                              title="Delete this note"
                            >
                              <Trash2 size={11} />
                            </Button>
                          </div>
                        </CardTitle>
                        <div className="text-[10px] text-muted-foreground flex items-center flex-wrap gap-x-3 gap-y-0.5">
                          <span><b>Bank:</b> {n.bank_name || '—'}</span>
                          <span><b>Period:</b> {n.statement_period || '—'}</span>
                          <span><b>Branch:</b> {branchName(n.branch_id)}</span>
                          {n.discrepancy_count > 0 && <span>{n.discrepancy_count} discrepancies</span>}
                          {n.total_gap > 0 && <span className="text-error">Gap: {fmtSar(n.total_gap)}</span>}
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="whitespace-pre-wrap text-xs leading-relaxed p-3 bg-white rounded-lg border border-emerald-100">
                          {n.narrative}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center text-[10px] text-muted-foreground italic pt-4 audit-trail-print-only">
          Generated by Smarto · {new Date().toLocaleString()} · {data.count || 0} note{data.count === 1 ? '' : 's'}
        </div>
      </div>
    </DashboardLayout>
  );
}
